import React from 'react';
import { cn } from '../../utils/cn';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import type { Basket, PositionRow } from '../../types';
import PnlFlash from '../common/PnlFlash';

interface BasketPnlChartProps {
  baskets: Basket[];
  positions: PositionRow[];
}

const BasketPnlChart: React.FC<BasketPnlChartProps> = ({ baskets, positions }) => {
  if (baskets.length === 0) return null;

  // Compute P&L per basket
  const pnlByBasket = new Map<string, number>();
  for (const p of positions) {
    pnlByBasket.set(p.basketId, (pnlByBasket.get(p.basketId) ?? 0) + (p.pnl ?? 0));
  }

  // Build rows with P&L, sorted by P&L descending
  const rows = baskets
    .map(b => ({
      id: b.basket_id,
      name: b.basket_name,
      pnl: pnlByBasket.get(b.basket_id) ?? 0,
      hasPositions: pnlByBasket.has(b.basket_id),
    }))
    .sort((a, b) => b.pnl - a.pnl);

  const maxAbsPnl = Math.max(...rows.map(r => Math.abs(r.pnl)), 1);

  return (
    <Card>
      <CardHeader>
        <CardTitle>P&L by Basket</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {rows.map(row => {
            const percentage = (Math.abs(row.pnl) / maxAbsPnl) * 100;
            const isPositive = row.pnl >= 0;

            return (
              <div key={row.id} className="flex items-center gap-3 px-4 py-2.5">
                <span className="text-sm text-gray-700 dark:text-gray-300 w-32 truncate flex-shrink-0">
                  {row.name}
                </span>
                <div className="flex-1 h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                  {row.hasPositions && (
                    <div
                      className={cn(
                        'h-full rounded-full transition-all',
                        isPositive ? 'bg-green-500' : 'bg-red-500'
                      )}
                      style={{ width: `${Math.max(percentage, 2)}%` }}
                    />
                  )}
                </div>
                <span className="w-24 text-right flex-shrink-0">
                  <PnlFlash
                    value={row.hasPositions ? row.pnl : null}
                    className="text-sm font-semibold"
                  />
                </span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default BasketPnlChart;
